import gql from 'graphql-tag';
import { getClient } from '../../apollo';
import { dateTimeToString, niceDateFromString, niceDateTimeFromString, niceTimeFromString } from '../../util/date';
import { getUser } from '../../util/sessions';
import { joinDefined } from '../../util/strings';
import { Arrangement, MortuaryLocation, ReferencePhoto } from '../funeral/FuneralFragments';
import { buildTabDataFragment } from '../../util/functions';
import { DocumentFields } from '../documentation/DocumentationFragments';
import Grave from '../fragments/Grave';
import moment from 'moment';

export const MortuaryItemFragment = buildTabDataFragment(
    [
        gql`
            fragment MortuaryItem on Funeral {
                ID
                LegacyKey
                Cancelled
                FirstName
                MiddleName
                Surname
                Gender

                DateOfBirth
                DateOfDeath1
                DateOfDeath2
                DateOfDeathType
                Age
                AgeCode
                
                IsBariatric
                IsInfectious
                IsDecomposed

                Certification {
                    ID
                    TypeOfBdmDeathCertificate
                    Referee {
                        ID
                    }
                    FileCorDisposalOrder {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileMedCertCause {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileStatDecForm {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileCorCremPermit {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileCremCertificate {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileCremBookingForm {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileCremContractForm {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileRefereeingDoctorForm {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileBurialBookingForm {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileEmbalmingCert {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                    FileDisclaimer {
                        ${DocumentFields}
                        RequiredDocument {
                            ID
                            Status
                        }
                    }
                }

                RefereeDoctorDateSigned
                RefereeCremationCheck
                RemovePacemaker
                RemovePacemakerPending

                ReadyForMortuary
                DateTransferredToMortuary
                DatetimeReadyForMortuaryCheckout
                DatetimeOfMortuaryCheckout
                MortuaryActionReturnedReason

                FuneralDressing {
                    ID
                    Status
                    Received
                    Comment
                    GreekShroud
                    Shroud
                    Date
                    Time
                }

                Disposal {
                    ID
                    CrematedOrBuried
                    Date
                    IsCoffinSelected
                    IsCoffinPrepared
                    Time
                    Start
                    LocationFlattened
                    Location {
                        ID
                        Name
                    }
                    DisposalBookingItems {
                        ID
                        Title
                        Quantity
                        Comment
                        Returns
                        Product {
                            ID
                            CoffinTrim
                            InternalItemID
                            Parent {
                                ... on ProductCategory {
                                    ID
                                    URLSegment
                                }
                                ... on CoreProductCategory {
                                    ID
                                    URLSegment
                                }
                            }
                            ProductCategories {
                                ... on ProductCategory {
                                    ID
                                    URLSegment
                                }
                                ... on CoreProductCategory {
                                    ID
                                    URLSegment
                                }
                            }
                        }
                        Variation {
                            ID
                            InternalItemID
                        }
                    }
                }

                Clergy {
                    ID
                    Contact {
                        ID
                        Name
                        ClergyCategory
                    }
                }

                RslMasonic {
                    ID
                    Contact {
                        ID
                        Name
                    }
                }

                ValuableItems {
                    ID
                    Description
                    Quantity
                    ToBePlaced
                    CurrentLocation
                    CurrentLocationDetail
                    WhereToBeReturned
                    ToBeReturned
                    FinalAction
                    Signed
                    Note
                }

                PlaceOfService {
                    ID
                    Date
                    Time
                    Comment
                    Start
                    Type
                    LocationFlattened
                    Location {
                        ID
                        Name
                    }
                }

                PlaceOfViewingRequired
                PlaceOfViewingItems {
                    ID
                    Date
                    Time
                    Comment
                    Start
                    Type
                    LocationFlattened
                    Location {
                        ID
                        Name
                    }
                }

                RefreshmentsVenue {
                    ID
                    Date
                    Time
                    Start
                    Type
                    LocationFlattened
                    Location {
                        ID
                        Name
                    }
                }

                Florists {
                    ID
                    Confirmed
                    Comment
                    Contact {
                        ID
                        Name
                    }
                    BookingItems {
                        edges {
                            node {
                                ID
                                Quantity
                                Comment
                                Product {
                                    ID
                                    ProductCategories {
                                        ... on ProductCategory {
                                            ID
                                            URLSegment
                                        }
                                        ... on CoreProductCategory {
                                            ID
                                            URLSegment
                                        }
                                    }
                                }
                                Variation {
                                    ID
                                }
                            }
                        }
                    }
                }
                DateOfService
                CoffinComment

                DeceasedPhotos {
                    ... on File {
                        ID
                        AbsoluteLink
                        Name
                    }
                    ... on Image {
                        ID
                        AbsoluteLink
                        Name
                    }
                }
                Arrangers {
                    ID
                    Member {
                        ID
                        FirstName
                        Surname
                    }
                }
                Coordinators {
                    ID
                    Member {
                        ID
                        FirstName
                        Surname
                    }
                }
                MortuaryWatchData
                MortuaryWatchChanged
            }
        `,
        Arrangement,
        ReferencePhoto,
        Grave,
        MortuaryLocation
    ],
    'Funeral'
);

const RemoveValuablesItemQuery = gql`
    mutation SaveFuneral($input: UpdateFuneralInput!) {
        updateFuneral(input: $input) {
            ID
            MortuaryWatchData
            MortuaryWatchChanged
            ValuableItems {
                ID
                Description
                Quantity
                ToBePlaced
                CurrentLocation
                CurrentLocationDetail
                WhereToBeReturned
                ToBeReturned
                FinalAction
                Signed
                Note
            }
        }
    }
`;

const ReleaseToCheckoutQuery = gql`
    mutation SaveFuneral($input: UpdateFuneralInput!) {
        updateFuneral(input: $input) {
            ID
            ReadyForMortuary
            DateTransferredToMortuary
            DatetimeReadyForMortuaryCheckout
            DatetimeOfMortuaryCheckout
            MortuaryActionReturnedReason
            MortuaryWatchData
            MortuaryWatchChanged
            EstimatedDeceasedWeight
        }
    }
`;

export const releaseToCheckout = funeralId => {
    const input = {
        ID: funeralId,
        DatetimeReadyForMortuaryCheckout: dateTimeToString(new Date())
    };

    return getClient()
        .mutate({ mutation: ReleaseToCheckoutQuery, variables: { input } })
        .then(
            ({ data }) => {
                return data.updateFuneral;
            },
            e => console.error('failed to get release to checkout', e)
        );
};

export const acknowledgeChanges = funeralId => {
    const input = {
        ID: funeralId,
        MortuaryWatchData: null,
        MortuaryActionReturnedReason: null
    };

    return getClient()
        .mutate({ mutation: ReleaseToCheckoutQuery, variables: { input } })
        .then(
            ({ data }) => {
                return data.updateFuneral;
            },
            e => console.error('failed to acknowledge changes', e)
        );
};

export const unreleaseFromCheckout = (funeralId, reason) => {
    const input = {
        ID: funeralId,
        DatetimeReadyForMortuaryCheckout: null,
        DatetimeOfMortuaryCheckout: null,
        MortuaryActionReturnedReason:
            dateTimeToString(new Date()) +
            ' released back from Mortuary Checkout by ' +
            joinDefined([getUser().FirstName, getUser().Surname], ' ') +
            ' because ' +
            reason
    };

    return getClient()
        .mutate({ mutation: ReleaseToCheckoutQuery, variables: { input } })
        .then(
            () => null,
            e => console.error('failed to get unrelease from checkout', e)
        );
};

export const releaseFromCheckout = (funeralId, weight) => {
    const input = {
        ID: funeralId,
        DatetimeOfMortuaryCheckout: dateTimeToString(new Date()),
        EstimatedDeceasedWeight: weight
    };

    return getClient()
        .mutate({ mutation: ReleaseToCheckoutQuery, variables: { input } })
        .then(
            ({ data }) => {
                return data.updateFuneral;
            },
            e => console.error('failed to get release from checkout', e)
        );
};

export const removeValuablesItemsAction = (ID, ValuableItems) => {
    const input = { ID, ValuableItems };
    return getClient()
        .mutate({ mutation: RemoveValuablesItemQuery, variables: { input } })
        .then(
            ({ data }) => data.updateFuneral,
            e => console.error('failed to remove items', e)
        );
};

export const getDressingStatus = form => {
    const status = form.getState('FuneralDressing.Status');
    const received = form.getState('FuneralDressing.Received');
    const GreekShroud = form.getState('FuneralDressing.GreekShroud');
    const Shroud = form.getState('FuneralDressing.Shroud');

    const viewings = form.getState('PlaceOfViewingItems') || [];
    const viewing = viewings.find(e => e.Type === 'Family Wish to Dress') || false;

    const thing =
        viewing &&
        joinDefined(
            [
                'Viewing where family wish to dress: ',
                niceDateTimeFromString(viewing.Start),
                !!viewing.LocationFlattened && 'at ' + viewing.LocationFlattened
            ],
            ' '
        );

    const result = () => {
        if (Shroud) return null;
        if (!status || status.length === 0) {
            return 'No dressing has been selected';
        } else if (
            status === 'Clothing collected at time of Arrangement' ||
            status === 'Clothing will be delivered to Funeral Home'
        ) {
            if (!!received) {
                return 'Clothing has been collected';
            } else if (status === 'Clothing will be delivered to Funeral Home') {
                const whenD = niceDateFromString(form.getState('FuneralDressing.Date'));
                const whenT = niceTimeFromString(form.getState('FuneralDressing.Time'));
                return status + ((!!whenD && ' on ' + joinDefined([whenD, whenT], ' at ')) || '');
            } else {
                return status;
            }
        }
        return status;
    };

    return joinDefined([result(), thing, GreekShroud && 'Use Greek Shroud', Shroud && 'Use Shroud'], '. ') + '.';
};

/**
 * Checks the mortuary watchlist for changes. Expect these fields:
 *  firstname, surname, gender, pacemaker, ?valuables, ?viewings, ?service, ?disposal, ?coffin, ?dressing
 * All fields are checked, any changed field is included in return array with a message and the old value.
 * @return {field: string, message: string, oldVal: mixed}[]
 */
export const listMortuaryChanges = form => {
    const changes = [];
    const hasData = form.getField('MortuaryWatchData');
    if (!!hasData) {
        try {
            JSON.parse(hasData);
        } catch (e) {
            return [];
        }
        const watchList = JSON.parse(hasData || '{}');

        /* check service details */
        const isGraveside = form.getField('PlaceOfService.Type') === 'Graveside';
        const oldServiceObj = ('service' in watchList && Object.values(watchList['service'])) || null;
        const oldService = (oldServiceObj && oldServiceObj[0] && oldServiceObj[0].split('|')) || ['', '', ''];
        const oldDisposalObj = ('disposal' in watchList && Object.values(watchList['disposal'])) || null;
        const oldDisposal = (oldDisposalObj && oldDisposalObj[0] && oldDisposalObj[0].split('|')) || ['', '', ''];
        if ('disposal' in watchList && isGraveside) {
            const newVal = niceDateTimeFromString(form.getField('Disposal.Start'));
            const oldVal = niceDateTimeFromString(oldService[1] === 'Graveside' ? oldDisposal[0] : oldService[0]);
            if (oldVal !== newVal) {
                changes.push({ field: 'service', message: 'Service details have changed.', oldVal });
            }
        } else if ('service' in watchList) {
            const newVal = niceDateTimeFromString(form.getField('PlaceOfService.Start'));
            const oldVal = niceDateTimeFromString(oldService[1] === 'Graveside' ? oldDisposal[0] : oldService[0]);
            if (oldVal !== newVal) {
                changes.push({ field: 'service', message: 'Service details have changed.', oldVal });
            }
        }

        /* check viewing details */
        const viewingItems = form.getField('PlaceOfViewingItems') || [];
        let oldViewing = null;
        let newViewing = null;
        if ('viewings' in watchList || viewingItems.length) {
            let newVal = null;
            let oldVal = null;
            if (viewingItems.length) {
                let latestDate = moment().add(100, 'years');
                viewingItems.forEach(e => {
                    const theDate = moment(e.Start || e.Date);
                    if (moment(theDate).isValid() && theDate < latestDate) {
                        latestDate = theDate;
                        newVal = niceDateTimeFromString(theDate);
                        newViewing = { Start: e.Start || e.Date, LocationFlattened: e.LocationFlattened };
                    }
                });
            }
            if ('viewings' in watchList) {
                const oldList = Object.values(watchList['viewings']) || [];
                let latestDate = moment().add(100, 'years');
                oldList.forEach(v => {
                    const obj = v.split('|');
                    const theDate = moment(obj[0]);
                    if (moment(theDate).isValid() && theDate < latestDate) {
                        latestDate = theDate;
                        oldVal = niceDateTimeFromString(theDate);
                        oldViewing = { Start: obj[0], LocationFlattened: obj[2] };
                    }
                });
            }
            if (oldVal !== newVal) {
                changes.push({ field: 'viewing', message: 'Viewing details have changed.', oldVal });
            }
        }

        /* check release details */
        let oldRelease =
            oldService[1] === 'Graveside'
                ? { Start: oldDisposal[0], LocationFlattened: oldDisposal[2] }
                : { Start: oldService[0], LocationFlattened: oldService[2] };
        if (!!oldViewing && moment(oldRelease.Start).isAfter(moment(oldViewing.Start))) {
            oldRelease = oldViewing;
        }
        let newRelease = (isGraveside ? form.getField('Disposal') : form.getField('PlaceOfService')) || {};
        if (!!newViewing && moment(newRelease.Start).isAfter(moment(newViewing.Start))) {
            newRelease = newViewing;
        }
        if (newRelease.LocationFlattened && oldRelease.LocationFlattened !== newRelease.LocationFlattened) {
            const oldVal = oldRelease.LocationFlattened;
            changes.push({ field: 'release', message: 'Release details have changed.', oldVal });
        }

        /* check coffin details */
        if (/*'coffin' in watchList || */ watchList) {
            const items = form.getField('Disposal.DisposalBookingItems') || [];
            let oldVal = '';
            const oldList = Object.values(watchList['coffin'] || {}) || [];
            if (oldList.length) {
                oldList.forEach(thing => {
                    if (thing && typeof thing === 'string') {
                        const obj = thing.split('|');
                        const item = items.find(o => o.InternalItemID === obj[0]);
                        if (!item) {
                            oldVal += obj[2] + ' was removed. ';
                        } else if (Number(item.Quantity) !== Number(obj[1])) {
                            oldVal += obj[2] + ' quantity was x' + obj[1] + '. ';
                        }
                    }
                });
            }
            if (items.length) {
                items.forEach(thing => {
                    const obj = oldList.find(
                        o => !!o && typeof o === 'string' && o.startsWith(thing.InternalItemID + '|')
                    );
                    if (!obj) oldVal += thing.Title + ' was added. ';
                });
            }
            if (!!oldVal) {
                changes.push({ field: 'coffins', message: 'Coffin details have changed.', oldVal });
            }
        }

        /* check dressing details */
        if (/*'dressing' in watchList || */ watchList) {
            const { Date, Time, Status, GreekShroud, Shroud } = form.getField('FuneralDressing') || {};
            const obj = (watchList['dressing'] || '').split('|');
            let oldVal = '';
            const datetime = joinDefined([Date, Time], ' ');
            if (!(obj[1] && obj[1] === Status)) {
                oldVal += obj[1] || '';
            }
            if (obj[0] !== datetime) {
                oldVal += niceDateTimeFromString(obj[0]) || '';
            }
            if (!!obj[2]) {
                oldVal += obj[2].includes('S') && !Shroud ? 'Use Shroud. ' : '';
                oldVal += obj[2].includes('G') && !GreekShroud ? 'Use Greek Shroud. ' : '';
            } else {
                oldVal += !!Shroud ? 'No Shroud. ' : '';
                oldVal += !!GreekShroud ? 'No Greek Shroud. ' : '';
            }
            if (oldVal) changes.push({ field: 'dressing', message: 'Dressing details have changed.', oldVal });
        }

        /* check valuables */
        if (/*'valuables' in watchList || */ watchList) {
            const valuableItems = form.getField('ValuableItems') || [];
            const oldList = ('valuables' in watchList && Object.keys(watchList['valuables'])) || [];
            const changeList = [];
            if (oldList.length) {
                oldList.forEach(vID => {
                    const obj = watchList['valuables'][vID].split('|');
                    const item = valuableItems.find(o => o.ID === vID);
                    if (item) {
                        if (
                            Number(obj[0]) !== item.Quantity ||
                            obj[1] !== (item.FinalAction || '') ||
                            obj[2] !== item.Description
                        ) {
                            changeList.push(`Was ${obj[2]} x${obj[0]} ${obj[1] || 'Unsure'}.`);
                        }
                    } else {
                        changeList.push(`${obj[2]} was removed.`);
                    }
                });
            }
            if (valuableItems && valuableItems.length) {
                valuableItems.forEach(item => {
                    const thing = oldList.includes(item.ID);
                    if (!thing) {
                        changeList.push(`${item.Description} was added.`);
                    }
                });
            }
            if (changeList.length) {
                changes.push({
                    field: 'valuables',
                    message: 'Valuables have been changed',
                    oldVal: joinDefined(changeList, ' ')
                });
            }
        }

        /* check personal/body details */
        if (watchList['surname'] !== form.getField('Surname')) {
            const oldVal = watchList['surname'];
            changes.push({ field: 'surname', message: 'Deceased Surname has changed.', oldVal });
        }
        if (watchList['firstname'] !== form.getField('FirstName')) {
            const oldVal = watchList['firstname'];
            changes.push({ field: 'firstname', message: 'Deceased First Name has changed.', oldVal });
        }
        if (watchList['gender'] !== form.getField('Gender')) {
            const oldVal = watchList['gender'];
            changes.push({ field: 'gender', message: 'Deceased Gender has changed.', oldVal });
        }
        const isCremation = form.getState('Disposal.CrematedOrBuried') === 'Cremated';
        if (!!watchList['pacemaker'] !== !!form.getField('RemovePacemaker')) {
            const oldVal = !!watchList['pacemaker']
                ? 'Had a pacemaker to remove.'
                : !!isCremation
                ? 'Pacemaker not present.'
                : 'Pacemaker does not need removal.';
            changes.push({ field: 'pacemaker', message: 'Pacemaker has changed.', oldVal });
        }
    }
    return changes;
};
